import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@lib/supabase';
import { streamFlowApi } from '@lib/streamFlowApi';

/**
 * TV-side pairing screen (`/tv/pair`).
 *
 * Customer just installed the StreamFlow app on a Shield/Fire TV and
 * has no session. We show a 6-char code + the URL to enter it (their
 * phone), poll the status endpoint every 2s, and on activation exchange
 * the redemption_token for a Supabase session via `setSession()`.
 * Then route to `/` (dashboard).
 *
 * Lifecycle:
 *   - On mount: POST /api/pairings → display code + start polling.
 *   - On `expired`: regenerate (small "Get a new code" button).
 *   - On `activated`: stop polling, redeem, persist session, navigate.
 *   - On unmount: stop polling.
 *
 * The 15-min TTL is generous; in practice activation happens in <60s.
 */

const POLL_INTERVAL_MS = 2000;

export default function TvPair() {
  const navigate = useNavigate();
  const [code, setCode] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [status, setStatus] = useState<'pending' | 'activated' | 'expired' | 'redeeming' | 'error' | 'subscription_required'>('pending');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPoll = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  // device_secret is held in-memory only — never persisted, never
  // shown on screen. Lost if the page reloads (which forces a fresh
  // code). That's the right tradeoff: keeping it ephemeral matches
  // its security purpose.
  const deviceSecretRef = useRef<string | null>(null);

  const startNew = async () => {
    stopPoll();
    setStatus('pending');
    setErrorMsg(null);
    setCode(null);
    deviceSecretRef.current = null;
    const res = await streamFlowApi.createPairing();
    if (!res.success || !res.data) {
      setStatus('error');
      setErrorMsg(res.error?.message ?? 'Could not generate a pairing code');
      return;
    }
    deviceSecretRef.current = res.data.device_secret;
    setCode(res.data.code);
    setExpiresAt(res.data.expires_at);

    // Start polling.
    pollTimerRef.current = setInterval(async () => {
      const secret = deviceSecretRef.current;
      if (!secret) return;
      const sres = await streamFlowApi.pollPairingStatus(res.data!.code, secret);
      if (!sres.success || !sres.data) return; // transient — keep polling
      const s = sres.data.status;
      if (s === 'pending') return;
      if (s === 'expired') {
        stopPoll();
        setStatus('expired');
        return;
      }
      if (s === 'redeemed') {
        // Edge case: code already redeemed (shouldn't happen unless
        // someone duplicated this device). Force a fresh code.
        stopPoll();
        setStatus('expired');
        return;
      }
      if (s === 'activated' && sres.data.redemption_token) {
        stopPoll();
        setStatus('redeeming');
        const rres = await streamFlowApi.redeemPairing(res.data!.code, sres.data.redemption_token, secret);
        if (!rres.success || !rres.data) {
          // 402 — the household's subscription lapsed. This is not a pairing
          // failure; "Try again" won't help because renewal happens off this
          // TV. Show large, TV-friendly copy pointing at the account page.
          if (rres.error?.code === 'SUBSCRIPTION_REQUIRED') {
            setStatus('subscription_required');
            return;
          }
          setStatus('error');
          setErrorMsg(rres.error?.message ?? 'Could not exchange the activation for a session');
          return;
        }
        // Persist the session so AuthContext picks it up. setSession
        // triggers onAuthStateChange in supabase-js → AuthContext sets
        // session → ProtectedRoute lets us through.
        const setRes = await supabase.auth.setSession({
          access_token: rres.data.access_token,
          refresh_token: rres.data.refresh_token,
        });
        if (setRes.error) {
          setStatus('error');
          setErrorMsg(setRes.error.message);
          return;
        }
        navigate('/', { replace: true });
      }
    }, POLL_INTERVAL_MS);
  };

  useEffect(() => {
    void startNew();
    return () => stopPoll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render large, high-contrast — we're showing this on a TV at 10ft.
  return (
    <div className="flex min-h-screen items-center justify-center bg-black p-12 text-white">
      <div className="w-full max-w-3xl text-center">
        <div className="mb-2 text-3xl font-light text-emerald-400">StreamFlow</div>
        <h1 className="mb-8 text-5xl font-bold">Pair this TV</h1>

        {status === 'error' && (
          <>
            <div className="mb-4 text-3xl text-rose-400">⚠ {errorMsg}</div>
            <button onClick={startNew} className="rounded-md bg-emerald-600 px-6 py-3 text-2xl font-medium hover:bg-emerald-500">
              Try again
            </button>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="mb-4 text-3xl text-amber-300">Code expired.</div>
            <button onClick={startNew} className="rounded-md bg-emerald-600 px-6 py-3 text-2xl font-medium hover:bg-emerald-500">
              Get a new code
            </button>
          </>
        )}

        {status === 'subscription_required' && (
          <>
            <div className="mb-6 text-4xl font-semibold text-amber-300">Subscription needed</div>
            <p className="mb-2 text-3xl text-zinc-200">
              Your StreamFlow subscription is inactive.
            </p>
            <p className="mb-8 text-3xl text-zinc-200">
              Renew at <span className="font-mono text-white">gastv.gasweb.info/account</span>, then pair again.
            </p>
            <button onClick={startNew} className="rounded-md bg-emerald-600 px-6 py-3 text-2xl font-medium hover:bg-emerald-500">
              Try again
            </button>
          </>
        )}

        {status === 'redeeming' && (
          <div className="text-3xl text-emerald-300">Signing in…</div>
        )}

        {status === 'pending' && code && (
          <>
            <p className="mb-8 text-2xl text-zinc-300">
              On your phone, go to <span className="font-mono text-white">gastv.gasweb.info/pair</span>
              {' '}and enter:
            </p>
            <div className="mb-10 inline-block rounded-2xl border-4 border-emerald-500 bg-zinc-900 px-12 py-8 font-mono text-9xl font-bold tracking-[0.3em] text-emerald-300">
              {code}
            </div>
            <p className="text-lg text-zinc-500">
              Code expires {expiresAt ? new Date(expiresAt).toLocaleTimeString() : 'in a few minutes'}.
              {' '}<button onClick={startNew} className="text-emerald-400 underline hover:text-emerald-300">Get a new code</button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
